/**
 * CoutsTab - TCO (Total Cost of Ownership) breakdown tab
 *
 * Montants : moteur KPI (`/kpis/costs/by-vehicle`, 8 familles) via useEntityCosts —
 * carburant, consommables, maintenance, assurance, leasing, sinistres, taxes, autres.
 * Aucun calcul de coût ici : les listes de drill-down servent à l'audit, pas au total.
 *
 * Features:
 * - Drill-down to detail view for audit purposes
 * - Period selector (30 jours, 90 jours, 12 mois — les périodes du moteur)
 *
 * ALL ACCORDIONS CLOSED BY DEFAULT
 */
import { formatMoney, currencySymbol } from '../../../utils/formatMoney.js';
import { useFormat } from '../../../i18n/format.js';
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback } from 'react';
import {
  Fuel, Wrench, Shield, AlertTriangle, Receipt,
  TrendingUp, Calendar, Euro, ChevronLeft, FileText,
  ExternalLink, Droplets, Landmark
} from 'lucide-react';
import { useEntityCosts, COST_CATEGORIES, COST_FAMILIES, PERIODS } from '../../../hooks/useEntityCosts.js';
import { useAccordionState } from '../../../hooks/useAccordionState.js';
import { AccordionGroup, AccordionContainer } from '../AccordionGroup.jsx';
import { EmptyState } from '../EntityDetailModal.jsx';

// Icon mapping
const ICON_MAP = {
  Fuel,
  Wrench,
  Shield,
  AlertTriangle,
  Receipt,
  Droplets,
  Landmark,
  FileText
};

// Argent : un seul outil (utils/formatMoney). La devise vient du backend
// (`costs.currency` : devise de la filiale, ou devise de référence en consolidé)
// et descend en prop jusqu'aux sous-composants — jamais d'« EUR » codé en dur.
const formatCurrency = (amount, currency) => formatMoney(amount, currency, { decimals: 0 });

// Format percentage
const formatPercent = (value) => {
  return `${Math.round(value || 0)}%`;
};

// Period selector
function PeriodSelector({ value, onChange }) {
  const periods = PERIODS;

  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
      {periods.map(p => (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            value === p.id
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// Cost category detail content
function FuelDetails({ data, onDrillDown, categoryId, currency }) {
  const { t, fmt } = useFormat();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.totalLiters')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.liters != null ? fmt.number(data.liters) : 0} L
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.avgPricePerLiter')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.avgPrice?.toFixed(2) || '0.00'} {currencySymbol(currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.fuelFillsLabel')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.count || 0}
          </p>
        </div>
      </div>
      {onDrillDown && data.count > 0 && (
        <button
          onClick={() => onDrillDown('fuel')}
          className="w-full mt-2 px-3 py-2 text-sm text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t('common.tabs.coutsTab.viewFuelLogs', { count: data.count })}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function MaintenanceDetails({ data, onDrillDown, currency }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.preventiveLabel')}</p>
          <p className="text-lg font-semibold text-green-600">
            {formatCurrency(data.preventive, currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.correctiveLabel')}</p>
          <p className="text-lg font-semibold text-amber-600">
            {formatCurrency(data.corrective, currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.interventionsLabel')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.count || 0}
          </p>
        </div>
      </div>
      {onDrillDown && data.count > 0 && (
        <button
          onClick={() => onDrillDown('maintenance')}
          className="w-full mt-2 px-3 py-2 text-sm text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t('common.tabs.coutsTab.viewInterventions', { count: data.count })}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function InsuranceDetails({ data, onDrillDown, currency }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.annualPremiumLabel')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {formatCurrency(data.annualPremium, currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.activeContractsLabel')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.contracts || 0}
          </p>
        </div>
      </div>
      {onDrillDown && data.contracts > 0 && (
        <button
          onClick={() => onDrillDown('insurance')}
          className="w-full mt-2 px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t('common.tabs.coutsTab.viewContracts', { count: data.contracts })}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function ClaimsDetails({ data, onDrillDown, currency }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.grossCostLabel')}</p>
          <p className="text-lg font-semibold text-red-600">
            {formatCurrency(data.grossCost, currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.reimbursedLabel')}</p>
          <p className="text-lg font-semibold text-green-600">
            -{formatCurrency(data.indemnified, currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('common.tabs.coutsTab.claimsLabel')}</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.count || 0}
          </p>
        </div>
      </div>
      {onDrillDown && data.count > 0 && (
        <button
          onClick={() => onDrillDown('claims')}
          className="w-full mt-2 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t('common.tabs.coutsTab.viewClaims', { count: data.count })}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function OtherDetails({ data, onDrillDown, currency }) {
  const { t } = useTranslation();
  const categoryLabels = {
    toll: t('common.tabs.coutsTab.categoryLabels.toll'),
    parking: t('common.tabs.coutsTab.categoryLabels.parking'),
    fine: t('common.tabs.coutsTab.categoryLabels.fine'),
    wash: t('common.tabs.coutsTab.categoryLabels.wash'),
    tax: t('common.tabs.coutsTab.categoryLabels.tax'),
    leasing: t('common.tabs.coutsTab.categoryLabels.leasing'),
    other: t('common.tabs.coutsTab.categoryLabels.other')
  };

  const nonZeroCategories = Object.entries(data.byCategory || {})
    .filter(([_, value]) => value > 0);

  return (
    <div className="space-y-3">
      {nonZeroCategories.length === 0 ? (
        <p className="text-sm text-gray-500 italic">{t('common.tabs.coutsTab.noExpenses')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {nonZeroCategories.map(([category, amount]) => (
              <div key={category} className="flex justify-between items-center py-1 px-2 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">{categoryLabels[category] || category}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(amount, currency)}</span>
              </div>
            ))}
          </div>
          {onDrillDown && data.count > 0 && (
            <button
              onClick={() => onDrillDown('other')}
              className="w-full mt-2 px-3 py-2 text-sm text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <FileText className="w-4 h-4" />
              {t('common.tabs.coutsTab.viewExpenses', { count: data.count })}
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Familles sans détail métier dédié (consommables, leasing, taxes) : montant + drill-down
function SimpleDetails({ data, onDrillDown, categoryId, currency }) {
  const { t } = useTranslation();
  const drillable = categoryId !== 'leasing' && onDrillDown && data.count > 0;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        {categoryId === 'leasing'
          ? t('common.tabs.coutsTab.leasingHint')
          : t('common.tabs.coutsTab.entriesOnPeriod', { count: data.count || 0 })}
      </p>
      {drillable && (
        <button
          onClick={() => onDrillDown(categoryId)}
          className="w-full mt-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t('common.tabs.coutsTab.viewEntries', { count: data.count })}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

const DETAILS_BY_FAMILY = {
  fuel: FuelDetails,
  maintenance: MaintenanceDetails,
  insurance: InsuranceDetails,
  claims: ClaimsDetails,
  other: OtherDetails
};

// Transaction list view for drill-down (audit purposes)
function TransactionListView({ category, rawData, period, onBack, currency }) {
  const { t, fmt } = useFormat();
  const categoryConfig = COST_CATEGORIES[category];
  const Icon = ICON_MAP[categoryConfig?.icon] || Receipt;

  // Get transactions based on category
  const transactions = React.useMemo(() => {
    switch (category) {
      case 'fuel':
      case 'consumable': {
        const CONSUMABLES = ['adblue', 'engine_oil', 'brake_fluid', 'coolant', 'washer_fluid'];
        return (rawData.fuelLogs || [])
          .filter(f => CONSUMABLES.includes(f.fuel_type) === (category === 'consumable'))
          .map(f => ({
          id: f.id,
          date: f.fuel_date,
          description: `${f.liters || 0}L @ ${f.price_per_liter || 0}${currencySymbol(currency)}/L`,
          location: f.station_name || f.location,
          amount: parseFloat(f.total_cost) || 0,
          reference: f.receipt_number
        }));
      }
      case 'maintenance':
        return (rawData.maintenance || [])
          .filter(m => m.status === 'completed')
          .map(m => ({
            id: m.id,
            date: m.completed_at || m.created_at,
            description: m.title || m.description || t('common.tabs.coutsTab.maintenanceFallbackDescription'),
            type: m.maintenance_type,
            amount: parseFloat(m.total_cost) || 0,
            reference: m.work_order_number
          }));
      case 'insurance':
        return (rawData.insurance || []).map(vi => ({
          id: vi.id,
          date: vi.assigned_at,
          description: t('common.tabs.coutsTab.insurancePolicyDescription', {
            number: vi._policy_number || vi.contract_id,
            insurer: vi._insurer_name ? ` (${vi._insurer_name})` : ''
          }),
          provider: vi._insurer_name || null,
          amount: Math.round(((parseFloat(vi.premium_amount) || 0) / 12) * 100) / 100,
          reference: t('common.tabs.coutsTab.insurancePolicyReference', {
            amount: formatCurrency(parseFloat(vi.premium_amount) || 0, currency)
          })
        }));
      case 'claims':
        return (rawData.claims || []).map(c => {
          const gross = parseFloat(c.montant_facture_garage) || parseFloat(c.montant_expertise) || parseFloat(c.estimated_amount) || 0;
          const indem = parseFloat(c.indemnity_amount) || 0;
          return {
            id: c.id,
            date: c.event_date || c.declared_at,
            description: c.description || c.type || t('common.tabs.coutsTab.claimFallbackDescription'),
            status: c.status,
            grossAmount: gross,
            indemnified: indem,
            amount: Math.max(gross - indem, 0),
            reference: c.claim_number
          };
        });
      case 'other':
      case 'tax':
        return (rawData.expenses || []).filter(e => (e.category === 'tax') === (category === 'tax')).map(e => ({
          id: e.id,
          date: e.expense_date || e.created_at,
          description: e.description || e.category,
          category: e.category,
          amount: parseFloat(e.gross_amount) || parseFloat(e.net_amount) || 0,
          reference: e.receipt_number
        }));
      default:
        return [];
    }
  }, [category, rawData, t]);

  // Sort by date descending
  const sortedTransactions = [...transactions].sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );

  const totalAmount = transactions.reduce((sum, tx) => sum + tx.amount, 0);

  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className={`p-2 rounded-lg bg-${categoryConfig?.color || 'gray'}-100`}>
          <Icon className={`w-5 h-5 text-${categoryConfig?.color || 'gray'}-600`} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{categoryConfig?.label || category}</h3>
          <p className="text-sm text-gray-500">
            {t('common.tabs.coutsTab.transactionsSummary', { count: transactions.length, total: formatCurrency(totalAmount, currency) })}
          </p>
        </div>
      </div>

      {/* Transaction list */}
      <div className="space-y-2">
        {sortedTransactions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">{t('common.tabs.coutsTab.noTransactionsPeriod')}</p>
          </div>
        ) : (
          sortedTransactions.map((tx, index) => (
            <div
              key={tx.id || index}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {tx.date ? fmt.date(tx.date, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      }) : t('common.states.notAvailable')}
                    </span>
                    {tx.reference && (
                      <span className="text-xs text-gray-400 font-mono">#{tx.reference}</span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {tx.description}
                  </p>
                  {tx.location && (
                    <p className="text-xs text-gray-500 mt-1">{tx.location}</p>
                  )}
                  {tx.provider && (
                    <p className="text-xs text-gray-500 mt-1">{tx.provider}</p>
                  )}
                  {tx.type && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      {tx.type}
                    </span>
                  )}
                  {tx.category && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded capitalize">
                      {tx.category}
                    </span>
                  )}
                </div>
                <div className="text-right ml-4">
                  <p className={`text-lg font-bold ${
                    category === 'claims' ? 'text-red-600' : `text-${categoryConfig?.color || 'gray'}-600`
                  }`}>
                    {formatCurrency(tx.amount, currency)}
                  </p>
                  {tx.grossAmount !== undefined && tx.indemnified !== undefined && tx.indemnified > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="text-red-500">{formatCurrency(tx.grossAmount, currency)}</span>
                      <span className="mx-1">-</span>
                      <span className="text-green-500">{formatCurrency(tx.indemnified, currency)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function CoutsTab({ entity, entityType, onMessage }) {
  const { t } = useTranslation();
  // View state: null = summary view, category string = drill-down view
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Fetch costs
  const { costs, rawData, loading, error, period, setPeriod, reload } = useEntityCosts(entityType, entity?.id);

  // Accordion state - ALL CLOSED by default
  const accordion = useAccordionState({ defaultExpanded: [] });

  // Drill-down handlers
  const handleDrillDown = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('common.tabs.coutsTab.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-2" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={reload}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          {t('common.actions.retry')}
        </button>
      </div>
    );
  }

  const { tco, breakdown, periodLabel, currency } = costs;

  if (tco === 0 && Object.values(breakdown).every(b => b.total === 0)) {
    return (
      <EmptyState
        icon={Euro}
        title={t('common.tabs.coutsTab.noCostRecordedTitle')}
        message={t('common.tabs.coutsTab.noCostRecordedMessage')}
      />
    );
  }

  // If viewing a specific category drill-down
  if (selectedCategory && rawData) {
    return (
      <TransactionListView
        category={selectedCategory}
        rawData={rawData}
        period={period}
        onBack={handleBack}
        currency={currency}
      />
    );
  }

  // Les 8 familles du moteur KPI, dans son ordre
  const categories = COST_FAMILIES.map(id => ({
    id,
    ...COST_CATEGORIES[id],
    data: breakdown[id],
    DetailsComponent: DETAILS_BY_FAMILY[id] || SimpleDetails
  }));

  return (
    <div className="space-y-4">
      {/* TCO Summary Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-emerald-100 text-sm">{t('common.tabs.coutsTab.tcoHeader')}</p>
            <p className="text-4xl font-bold mt-1">{formatCurrency(tco, currency)}</p>
            <p className="text-emerald-100 text-sm mt-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('common.tabs.coutsTab.periodLabelPrefix', { period: periodLabel })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <TrendingUp className="w-12 h-12 text-emerald-200" />
          </div>
        </div>

        {/* Period Selector */}
        <div className="mt-4">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Cost Breakdown Accordions */}
      <AccordionContainer>
        {categories.map(({ id, label, icon, color, data, DetailsComponent }) => {
          const Icon = ICON_MAP[icon] || Receipt;
          const hasData = data.total > 0 || data.count > 0;

          return (
            <AccordionGroup
              key={id}
              id={id}
              title={label}
              count={null}
              icon={Icon}
              color={color}
              isExpanded={accordion.isExpanded(id)}
              onToggle={() => accordion.toggleGroup(id)}
              summary={`${formatCurrency(data.total, currency)} - ${formatPercent(data.percentage)}`}
              headerRight={
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(data.total, currency)}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded-full bg-${color}-100 text-${color}-800`}>
                    {formatPercent(data.percentage)}
                  </span>
                </div>
              }
              emptyMessage={t('common.states.empty')}
            >
              {hasData ? (
                <DetailsComponent data={data} onDrillDown={handleDrillDown} categoryId={id} currency={currency} />
              ) : (
                <p className="text-sm text-gray-500 italic">{t('common.tabs.coutsTab.noDataForPeriod')}</p>
              )}
            </AccordionGroup>
          );
        })}
      </AccordionContainer>

      {/* Source note */}
      <div className="text-center text-xs text-gray-500 mt-4">
        {costs.source === 'kpi'
          ? t('common.tabs.coutsTab.kpiSourceNote')
          : t('common.tabs.coutsTab.driverSourceNote')}
      </div>
    </div>
  );
}

export default CoutsTab;
